import {
  Article,
  FeedArticle,
  BettingNFLEvent,
  FeedCategory,
  FeedItem,
  FunFacts,
  NFLMismatch,
  NFLTeam,
  RankWithPlayerId,
  NFLTeamRanking,
  PlayerPropWithGradeAnalysis,
  Insight,
  LandingPageContent,
  SportsbookType,
  BigBoardMetaResponse,
  BigBoardResponse,
  FootballPlayer,
  FootballPlayerWithGradesTeamAndDraftInfo,
  RecentDefensesForTeam,
  CurrentWeekSDKEvent,
  EventMarketData,
  ConnectInsight,
  type PlayerInsightsByWeek,
  ConnectInsightWithPlayer,
  GameScript,
  SDKTopPlayerProp,
  PaginatedPlayerPropBets,
  PlayerPropSection,
  InsightsUpdatedTimestamp,
  TeamDepthChart,
  NFLWeekAbbreviation,
  SDKWeek,
  SDKEvent,
  MixedRosterItem,
  SDKReceivingMetricResponse,
  SDKRushingMetricResponse,
  SDKPlayerHalftimeAvgRushingMetricResponse,
  BiggestPlayerMismatchMap,
  PlayerPropStatMap,
  FANTASY_CONSTANTS,
  FantasyLeagueType,
  FantasyPosition,
  FantasyPositions,
  PlayerHistoricalPerformance,
  PlayerMatchup,
  PlayerPropKey,
  SDKPlayerPropGradeAnalysis,
  FantasyScoringTypeEnum,
  FantasyScoringType,
  PlayerPositionGrade,
  SDKPlayerHalftimeAvgReceivingMetricResponse,
  SDKPreGameNarrativesResponse,
  SDKNarrativesResponse,
  SDKPassRateNarrativesResponse,
  SDKPressureRateNarrativeResponse,
  PastPerformanceResults,
  PastPerformanceEvent,
  PlayerPropsByWeekEvent,
  type InjuryData,
  FootballGameV2,
  type PremiumStatsPreviewKeyStatsResponse,
  type PremiumStatsPreviewKeyStatsParams,
  type FantasyWeekProjection,
  FantasyWeekProjectionResponse,
  FantasyWeekProjectionParams,
  PlayersInjuryRecord,
  PlayerCareer,
  RunningBackType,
  FantasyMatchupPosition,
  FootballPlayerWithGradesAndTeam,
  DraftEligiblePlayer,
  SUPER_BOWL_EVENT_STATUS,
  WaiverTargets,
  MappedGradesByGameResponse,
  GetTeamsParams,
  PlayerLeague,
  type PlayerProfileBaseDataResponse,
  GetPlayerPropsByWeekResponse,
  WeeklyRankingsResponse,
  RankingsResponse,
  InSeasonFiltersEntity,
  FiltersEntity,
  PlayerFiltersEntity,
  GetSeasonGamesParams,
  GetPlayerPropsParams,
} from '@pff-consumer/schema'
import { Matchup } from '@pff-consumer/schema/sdk/matchups'
import {
  type ReceiverMatchups,
  type PlayerVsTeamMatchup as SDKPlayerMatchup,
  type GradeFacet,
  League,
  PlayerPosition,
} from '@pro-football-focus/pffkit-js-core'
import { SerializedError } from '@reduxjs/toolkit'
import { BaseQueryFn, createApi, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query/react'
import {
  bettingEndpoints,
  connectEndpoints,
  contentEndpoints,
  fantasyEndpoints,
  footballEndpoints,
} from '@pff-consumer/api-sdk'
import {
  combinePaginatedResults,
  envConfig,
  getFilteredMatchups,
  getInsightWithPlayer,
  getGameScriptWithPlayer,
  getPlayerByPosition,
  getPropBetsWithAnalysis,
  groupPropBetsByPropKey,
  transformPlayerPropBetsResponse,
  transformRankingsResponse,
  transformWaiverTargetsResponse,
  transformWeeklyRankingsResponse,
  transformPlayerInjuryStatusResponse,
  getBaseUrl,
} from '@pff-consumer/utils'
import {
  SINGLE_MARKET_PAGE_SIZE,
  OVER_UNDER_MARKET_PAGE_SIZE,
  PROPS_WITH_OVER_UNDER,
} from '@pff-consumer/utils/lib/bet/player-prop-constants'
import { PffKitService, PFFKitError, mapPffKitPositionToFantasyPosition } from '@pff-consumer/pff-kit-service'
import { filterMismatches } from './temp/filter-mismatches'
import { addEventMarketsDataToEvents } from './transformers/add-event-markets-data-to-event'
import { addOverallGradeToMatchups } from './transformers/add-overall-grade-to-matchups'
import { isHydrateAction } from '../helpers/hydrateAction'
import { addActiveSeasonsForRookie } from './transformers/add-active-seasons-for-rookie'

const { CONSUMER_API_URL, CONSUMER_API_KEY } = envConfig

const getAuthTokenFromState = (state: unknown): string | undefined => {
  // @ts-ignore
  return state?.account?.token
}

const baseUrl = getBaseUrl()

const baseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError | SerializedError | undefined,
  // this should be `{ useAccountToken?: boolean } | undefined,`
  // but RTK will break so I'm leaving it off for now
  { useMerlinProxyForWeb?: boolean },
  {}
> = (...baseQueryArgs) => {
  const [, , extraOptions] = baseQueryArgs
  const { useMerlinProxyForWeb } = extraOptions || {}
  return fetchBaseQuery({
    baseUrl: useMerlinProxyForWeb ? baseUrl : CONSUMER_API_URL,
    prepareHeaders: (headers, { getState }) => {
      // We need to get the auth/jwt token from the store and add to the headers for every api call
      const authToken = getAuthTokenFromState(getState())
      const apiKey = CONSUMER_API_KEY
      const isDev = process.env.NODE_ENV === 'development'

      if (authToken) {
        headers.set('Authorization', authToken)

        // This is temp right now to move to using the new JWT
        // This will override the jwt-token parameter that gets passed
        // into cloudfront
        headers.set('jwt-token', authToken)
      }

      headers.set('Api-Key', apiKey)

      if (isDev && process.env.LOCAL_JWT_TOKEN) {
        headers.set('Authorization', `Bearer ${process.env.LOCAL_JWT_TOKEN}`)
      }
      return headers
    },
  })(...baseQueryArgs)
}

// Define a service using a base URL and expected endpoints
export const consumerApi = createApi({
  reducerPath: 'consumerApi',
  baseQuery,
  extractRehydrationInfo(action, { reducerPath }) {
    if (isHydrateAction(action)) {
      return action.payload[reducerPath]
    }
  },
  endpoints: (builder) => ({
    getFeedCategories: builder.query<FeedCategory[], { client: string }>({
      query: contentEndpoints.feedCategories,
      transformResponse: (response: { feedCategories: FeedCategory[] }) => {
        return response.feedCategories ?? []
      },
    }),
    // prettier-ignore
    getFeedItems: builder.query<FeedItem[], { feedCategorySlug: string | undefined; start: number; limit: number; showPremium: boolean; client: string }>({
      query: contentEndpoints.feed,
      transformResponse: (response: { feed: FeedItem[] }) => {
        return response.feed ?? []
      },
    }),
    getArticleByIdOrSlug: builder.query<
      Article | null,
      ({ slug: string } | { id: number }) & {
        client: string
        isLoggedIn: boolean
        isPremium: boolean
      }
    >({
      query: (
        params: ({ slug: string } | { id: number }) & {
          client: string
          isLoggedIn: boolean
          isPremium: boolean
        }
      ) => {
        // [SIM-848] We're passing in isLoggedIn & isPremium to break any potential caching or paywalled articles.
        // Since logged in/out & premium user requests would otherwise be the same, it doesn't return all the data if logged out going to logged in
        if ('id' in params) {
          return contentEndpoints.articleById({
            id: params.id,
            client: params.client,
            isLoggedIn: params.isLoggedIn,
            isPremium: params.isPremium,
          })
        }

        return contentEndpoints.articleBySlug({
          slug: params.slug,
          client: params.client,
          isLoggedIn: params.isLoggedIn,
          isPremium: params.isPremium,
        })
      },
      transformResponse: (response: Article | { articles: Article[] }) => {
        // Single Article with an id
        if ('id' in response) {
          return response ?? null
        }

        // Object with articles property
        if (Array.isArray(response?.articles)) {
          return response?.articles?.[0] ?? null
        }

        return null
      },
    }),
    getLatestArticles: builder.query<Article[], { client: string }>({
      query: contentEndpoints.latestArticles,
      transformResponse: (response: { articles: Article[] }) => {
        return response.articles ?? []
      },
    }),
    getBookmarkedArticles: builder.query<FeedArticle[], { ids: number[]; client: string }>({
      query: contentEndpoints.bookmarkedArticles,
      transformResponse: (response: { articles: FeedArticle[] }) => {
        return response.articles ?? []
      },
    }),
    getLandingPageContent: builder.query<LandingPageContent, { pageTopic: string }>({
      query: contentEndpoints.ladningPageContent,
      transformResponse: (response: { landingPage: LandingPageContent }) => {
        return response.landingPage
      },
    }),
    getCurrentWeekAndEvents: builder.query<CurrentWeekSDKEvent, { sportsbookId: string; scoreData?: boolean }>({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        try {
          const getCurrentWeek = await fetchWithBQ(footballEndpoints.getCurrentEventWeek({ league: PlayerLeague.NFL }))
          const { sportsbookId, scoreData } = arg
          if (getCurrentWeek.error) return { error: getCurrentWeek.error }

          const currentEventWeek = getCurrentWeek.data as SDKWeek
          const { abbreviation: weekAbv, season } = currentEventWeek

          const events = await PffKitService.getEvents(season - 1, sportsbookId, weekAbv, scoreData)

          return { data: { currentWeekEvents: events, currentEventWeek } }
        } catch (e: PFFKitError | any) {
          return e instanceof PFFKitError ? { error: e.message } : { error: e }
        }
      },
      keepUnusedDataFor: 7200,
    }),
    getPlayerMatchupsForEvent: builder.query<PlayerMatchup, { playerId: number; eventId: number }>({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        // Get player matchups for event
        const playerMatchups = await fetchWithBQ(
          footballEndpoints.getPlayerMatchupsForEvent({ playerId: arg.playerId, eventId: arg.eventId })
        )
        if (playerMatchups.error) return { error: playerMatchups.error }

        const data: PlayerMatchup = playerMatchups.data as PlayerMatchup
        const playerIdsToFetch = data.matchups.map((player) => player.playerId)

        const opponentPlayerRequest = await fetchWithBQ(footballEndpoints.getPlayersById({ ids: playerIdsToFetch }))
        if (opponentPlayerRequest.error) return { error: opponentPlayerRequest.error }

        const opponentPlayerData = opponentPlayerRequest.data as { players: FootballPlayer[] }

        data.matchups.forEach((matchup) => {
          const extraData = opponentPlayerData.players.find((player) => player.playerId === matchup.playerId)

          /* eslint-disable no-param-reassign */
          if (extraData) {
            matchup.firstName = extraData.firstName
            matchup.lastName = extraData.lastName
            matchup.position = extraData.position || ''
          }
          /* eslint-enable no-param-reassign */
        })
        return { data: playerMatchups.data } as { data: PlayerMatchup }
      },
      keepUnusedDataFor: 7200,
    }),
    getSDKPlayerMatchupsForWeek: builder.query<
      { matchups: SDKPlayerMatchup[]; currentEventWeek: SDKWeek },
      { starters?: MixedRosterItem[]; bench?: MixedRosterItem[]; position?: PlayerPosition; facet?: GradeFacet }
    >({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        try {
          const getCurrentWeek = await fetchWithBQ(footballEndpoints.getCurrentEventWeek({ league: PlayerLeague.NFL }))
          if (getCurrentWeek.error) return { error: getCurrentWeek.error }
          const currentEventWeek = getCurrentWeek.data as SDKWeek
          const { abbreviation: weekAbv, season } = currentEventWeek

          const matchups = await PffKitService.getMatchupsForWeek(season, weekAbv)

          const filteredMatchups: SDKPlayerMatchup[] = getFilteredMatchups(
            matchups,
            arg.starters,
            arg.bench,
            arg.position,
            arg.facet
          )

          return { data: { matchups: filteredMatchups, currentEventWeek } }
        } catch (e: PFFKitError | any) {
          return e instanceof PFFKitError ? { error: e.message } : { error: e }
        }
      },
    }),
    getSDKPlayerMatchups: builder.query<{ matchups: SDKPlayerMatchup[] }, { playerId: number; eventId: number }>({
      async queryFn(arg, _queryApi, _extraOptions) {
        try {
          const matchups = await PffKitService.getMatchupsForPlayer(arg.playerId, arg.playerId)

          return { data: { matchups } }
        } catch (e: PFFKitError | any) {
          return e instanceof PFFKitError ? { error: e.message } : { error: e }
        }
      },
    }),
    getSDKReceiverMatchupsForWeek: builder.query<
      { matchups: ReceiverMatchups[] | []; currentEventWeek: SDKWeek },
      { starters?: MixedRosterItem[]; bench?: MixedRosterItem[] }
    >({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        try {
          const getCurrentWeek = await fetchWithBQ(footballEndpoints.getCurrentEventWeek({ league: PlayerLeague.NFL }))
          if (getCurrentWeek.error) return { error: getCurrentWeek.error }
          const currentEventWeek = getCurrentWeek.data as SDKWeek
          const { abbreviation: weekAbv, season } = currentEventWeek
          const matchups = await PffKitService.getReceiverMatchupsForWeek(season, weekAbv)

          if (arg.starters && arg.bench) {
            const wideReceivers = [
              ...getPlayerByPosition(arg.starters, FantasyPosition.WR),
              ...getPlayerByPosition(arg.bench, FantasyPosition.WR),
            ]

            const filteredMatchups = matchups.filter((matchup: ReceiverMatchups) =>
              wideReceivers.includes(matchup.receiver.id)
            )

            return { data: { matchups: filteredMatchups, currentEventWeek } }
          }

          return { data: { matchups, currentEventWeek } }
        } catch (e: PFFKitError | any) {
          return e instanceof PFFKitError ? { error: e.message } : { error: e }
        }
      },
    }),
    getSDKReceiverMatchups: builder.query<{ matchups: ReceiverMatchups }, { playerId: number; eventId: number }>({
      async queryFn(arg, _queryApi, _extraOptions) {
        try {
          const matchups = await PffKitService.getMatchupsForReceiver(arg.playerId, arg.eventId)

          return { data: { matchups } }
        } catch (e: PFFKitError | any) {
          return e instanceof PFFKitError ? { error: e.message } : { error: e }
        }
      },
    }),
    getSDKMatchups: builder.query<
      { matchups: Matchup[]; currentEventWeek: SDKWeek },
      {
        position: PlayerPosition | FantasyMatchupPosition
        starters?: MixedRosterItem[]
        bench?: MixedRosterItem[]
        runningBackType?: RunningBackType
      }
    >({
      keepUnusedDataFor: 7200,
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        try {
          const getCurrentWeek = await fetchWithBQ(footballEndpoints.getCurrentEventWeek({ league: PlayerLeague.NFL }))
          if (getCurrentWeek.error) return { error: getCurrentWeek.error }
          const currentEventWeek = getCurrentWeek.data as SDKWeek
          const { abbreviation: weekAbv, season } = currentEventWeek
          let matchups: Matchup[] = []
          const fantasyPlayerPositionMap = {
            [FantasyMatchupPosition.QB]: PlayerPosition.Quarterback,
            [FantasyMatchupPosition.WR]: PlayerPosition.WideReceiver,
            [FantasyMatchupPosition.TE]: PlayerPosition.TightEnd,
          }

          const playerPosition =
            fantasyPlayerPositionMap[arg.position as keyof typeof fantasyPlayerPositionMap] || arg.position

          if (arg.position === FantasyMatchupPosition.RB && arg.runningBackType) {
            matchups = await PffKitService.getMatchupsForRunningBack(arg.runningBackType, season, weekAbv)
          } else {
            matchups = await PffKitService.getMatchups(playerPosition, season, weekAbv)
            if (arg.starters && arg.bench) {
              const players = [
                ...getPlayerByPosition(arg.starters, mapPffKitPositionToFantasyPosition(playerPosition)),
                ...getPlayerByPosition(arg.bench, mapPffKitPositionToFantasyPosition(playerPosition)),
              ]

              const filteredMatchups = matchups.filter((matchup: Matchup) => players.includes(matchup.player.id))

              return { data: { matchups: filteredMatchups, currentEventWeek } }
            }
          }

          // overallGrade is added for sorting purpose
          const matchupsList = addOverallGradeToMatchups(matchups)
          // Hacky, remove undefined since they aren't serializable
          const matchupsNoUndefined = matchupsList.map((matchup) => JSON.parse(JSON.stringify(matchup)))

          return { data: { matchups: matchupsNoUndefined, currentEventWeek } }
        } catch (e: PFFKitError | any) {
          return e instanceof PFFKitError ? { error: e.message } : { error: e }
        }
      },
    }),
    getSDKEvents: builder.query<
      BettingNFLEvent[],
      { sportsbookId?: string; season?: number; weekAbv?: string; scoreData?: boolean }
    >({
      query: footballEndpoints.getSDKEvents,
      transformResponse: (response: { events: BettingNFLEvent[] }) => response.events ?? [],
    }),
    getSDKEventsById: builder.query<BettingNFLEvent, { eventId: number; sportsbookId?: string; scoreData?: boolean }>({
      query: footballEndpoints.getSDKEventsById,
      transformResponse: (response: { event: BettingNFLEvent }) => response.event ?? {},
    }),
    getSDKEventsWithMarketById: builder.query<
      Partial<BettingNFLEvent> | null,
      { eventId: number; sportsbookId: SportsbookType; scoreData?: boolean }
    >({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        try {
          const { eventId, sportsbookId, scoreData } = arg

          const eventData = await PffKitService.getEvent(eventId, sportsbookId, scoreData)

          if (!eventData) {
            return { data: eventData }
          }

          const getEventsMarketDataById = (await fetchWithBQ(
            footballEndpoints.getEventsMarketDataById({
              eventId: arg.eventId,
              sportsbookId: arg.sportsbookId,
            })
          )) as { data: { markets: EventMarketData[] }; error?: FetchBaseQueryError }

          if (getEventsMarketDataById.error) {
            return { error: getEventsMarketDataById.error }
          }
          const eventsMarketData = getEventsMarketDataById.data.markets

          return { data: addEventMarketsDataToEvents([eventData], eventsMarketData, arg.sportsbookId)[0] }
        } catch (e: PFFKitError | any) {
          return e instanceof PFFKitError ? { error: e.message } : { error: e }
        }
      },
      keepUnusedDataFor: 7200,
    }),
    getSDKMismatchesByEvent: builder.query<
      BiggestPlayerMismatchMap,
      { sportsbookId: string; eventId: number; markets: PlayerPropKey[] }
    >({
      keepUnusedDataFor: 7200,
      async queryFn({ sportsbookId, eventId, markets }) {
        try {
          const mismatches = await PffKitService.getPlayerMismatchesForEvent(eventId, sportsbookId, markets)
          const mismatchObject: BiggestPlayerMismatchMap = {}
          mismatches.forEach((mismatch) => {
            mismatchObject[mismatch.market] = mismatch.playerId
          })

          return { data: mismatchObject }
        } catch (e: PFFKitError | any) {
          return e instanceof PFFKitError ? { error: e.message } : { error: e }
        }
      },
    }),
    getSDKMismatchesBySeasonWeek: builder.query<
      BiggestPlayerMismatchMap,
      { sportsbookId: string; season?: number; weekAbv?: NFLWeekAbbreviation; markets: PlayerPropKey[] }
    >({
      keepUnusedDataFor: 7200,
      async queryFn({ sportsbookId, season, weekAbv, markets }) {
        try {
          const mismatches = await PffKitService.getPlayerMismatchesForWeek(season, weekAbv, sportsbookId, markets)
          const mismatchObject: BiggestPlayerMismatchMap = {}
          mismatches.forEach((mismatch) => {
            mismatchObject[mismatch.market] = mismatch.playerId
          })
          return { data: mismatchObject }
        } catch (e: PFFKitError | any) {
          return e instanceof PFFKitError ? { error: e.message } : { error: e }
        }
      },
    }),
    getSDKHotPlayersByEvent: builder.query<
      PlayerPropStatMap,
      { sportsbookId: string; eventId: number; markets: PlayerPropKey[] }
    >({
      keepUnusedDataFor: 7200,
      async queryFn({ sportsbookId, eventId, markets }) {
        try {
          const hotPlayers = await PffKitService.getHotPlayersForEvent(eventId, sportsbookId, markets)
          const statMap = hotPlayers.reduce((acc: PlayerPropStatMap, curr) => {
            const map = { ...acc }
            map[curr.market] = {
              ...map[curr.market],
              [curr.playerId]: [...(acc[curr.market]?.[curr.playerId] || []), Number(curr.value)],
            }
            return map
          }, {})

          return { data: statMap }
        } catch (e: PFFKitError | any) {
          return e instanceof PFFKitError ? { error: e.message } : { error: e }
        }
      },
    }),
    getSDKHotPlayersBySeasonWeek: builder.query<
      PlayerPropStatMap,
      { sportsbookId: string; season?: number; weekAbv?: NFLWeekAbbreviation; markets: PlayerPropKey[] }
    >({
      keepUnusedDataFor: 7200,
      async queryFn({ sportsbookId, season, weekAbv, markets }) {
        try {
          const hotPlayers = await PffKitService.getHotPlayersForWeek(season, weekAbv, sportsbookId, markets)
          const statMap = hotPlayers.reduce((acc: PlayerPropStatMap, curr) => {
            const map = { ...acc }
            map[curr.market] = {
              ...map[curr.market],
              [curr.playerId]: [...(acc[curr.market]?.[curr.playerId] || []), Number(curr.value)],
            }
            return map
          }, {})

          return { data: statMap }
        } catch (e: PFFKitError | any) {
          return e instanceof PFFKitError ? { error: e.message } : { error: e }
        }
      },
    }),
    getSDKReceivingMetrics: builder.query<SDKReceivingMetricResponse, { season: number; weekAbv: string }>({
      query: footballEndpoints.getSDKReceivingMetrics,
    }),
    getSDKHalftimeReceivingMetrics: builder.query<
      SDKPlayerHalftimeAvgReceivingMetricResponse,
      { season?: number; weekAbv: string }
    >({
      query: footballEndpoints.getSDKHalftimeReceivingMetrics,
    }),
    getSDKRushingMetrics: builder.query<SDKRushingMetricResponse, { season: number; weekAbv: string }>({
      query: footballEndpoints.getSDKRushingMetrics,
    }),
    getPreGamePassNarratives: builder.query<SDKPreGameNarrativesResponse, { season: number; weekAbv: string }>({
      query: footballEndpoints.getSDKPFFLiveSeasonPassRateNarratives,
    }),
    getPFFLiveData: builder.query<
      {
        receiving: SDKReceivingMetricResponse
        rushing: SDKRushingMetricResponse
        narratives: SDKNarrativesResponse
      },
      { season: number; weekAbv: string }
    >({
      // @ts-expect-error
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        try {
          const receivingMetrics = await fetchWithBQ(
            footballEndpoints.getSDKReceivingMetrics({
              season: arg.season,
              weekAbv: arg.weekAbv,
            })
          )

          if (receivingMetrics.error) return { error: receivingMetrics.error } as unknown as FetchBaseQueryError

          const rushingMetrics = await fetchWithBQ(
            footballEndpoints.getSDKRushingMetrics({
              season: arg.season,
              weekAbv: arg.weekAbv,
            })
          )

          if (rushingMetrics.error) return { error: rushingMetrics.error } as unknown as FetchBaseQueryError

          const passRateNarratives = await fetchWithBQ(
            footballEndpoints.getSDKPFFPassRateNarratives({
              season: arg.season,
              weekAbv: arg.weekAbv,
            })
          )

          if (passRateNarratives.error) return { error: passRateNarratives.error } as unknown as FetchBaseQueryError

          const pressureRateNarrtivates = await fetchWithBQ(
            footballEndpoints.getSDKPFFPressureRateNarratives({
              season: arg.season,
              weekAbv: arg.weekAbv,
            })
          )

          if (pressureRateNarrtivates.error)
            return { error: pressureRateNarrtivates.error } as unknown as FetchBaseQueryError

          return {
            data: {
              narratives: {
                passRate: passRateNarratives.data as SDKPassRateNarrativesResponse,
                pressureRate: pressureRateNarrtivates.data as SDKPressureRateNarrativeResponse,
              },
              receiving: receivingMetrics.data as SDKReceivingMetricResponse,
              rushing: rushingMetrics.data as SDKRushingMetricResponse,
            },
          }
        } catch (e) {
          return { error: e }
        }
      },
      keepUnusedDataFor: 10,
    }),
    getSDKHalftimeRushingMetrics: builder.query<
      SDKPlayerHalftimeAvgRushingMetricResponse,
      { season?: number; weekAbv: string }
    >({
      query: footballEndpoints.getSDKHalftimeRushingMetrics,
    }),
    getSDKPFFPassRateNarratives: builder.query<unknown, { season: number; weekAbv: string }>({
      query: footballEndpoints.getSDKPFFPassRateNarratives,
    }),
    getSDKPFFPressureRateNarratives: builder.query<unknown, { season: number; weekAbv: string }>({
      query: footballEndpoints.getSDKPFFPressureRateNarratives,
    }),
    getPlayerProfileHistoricalPerformanceData: builder.query<
      PastPerformanceResults,
      { season: number; weekAbv: string; playerId: string }
    >({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        const pastPerformanceRequest = fetchWithBQ(
          footballEndpoints.getSDKPastPerformanceEvents({
            season: arg.season,
            weekAbv: arg.weekAbv,
            playerId: arg.playerId,
          })
        )

        const similarDefensesRequest = fetchWithBQ(
          footballEndpoints.getSDKPastPerformanceSimiliarDefensesEvents({
            season: arg.season,
            weekAbv: arg.weekAbv,
            playerId: arg.playerId,
          })
        )

        const playerPropBetsRequest = fetchWithBQ(
          footballEndpoints.getPlayerPropBets({ sportsbookId: 'FD', season: arg.season, weekAbv: arg.weekAbv })
        )

        const [pastPerformances, similiarDefenses, playerPropBets] = await Promise.all([
          pastPerformanceRequest,
          similarDefensesRequest,
          playerPropBetsRequest,
        ])
        if (pastPerformances.error) return { error: pastPerformances.error }
        if (similiarDefenses.error) return { error: similiarDefenses.error }
        if (playerPropBets.error) return { error: playerPropBets.error }

        // we only need a single player's prop bets, without this we put a lot of unnecessary data in our store
        // and unnecessarily increase the size of of our SSR response
        const playerProps = (playerPropBets.data as PlayerPropsByWeekEvent)?.playerProps?.filter(
          (bet) => `${bet.playerId}` === arg.playerId
        )

        const results: PastPerformanceResults = {
          pastPerformance: pastPerformances.data as PastPerformanceEvent,
          similarDefense: similiarDefenses.data as PastPerformanceEvent,
          playerPropData: { playerProps },
        }

        return { data: results }
      },
      keepUnusedDataFor: 7200,
    }),
    getCurrentEventWeekPlayerPropsAnalysis: builder.query<
      SDKPlayerPropGradeAnalysis[],
      { sportsbookId: string; detailed?: boolean }
    >({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        const getCurrentWeek = await fetchWithBQ(footballEndpoints.getCurrentEventWeek({ league: PlayerLeague.NFL }))
        if (getCurrentWeek.error) return { error: getCurrentWeek.error }
        const currentEventWeek = getCurrentWeek.data as SDKWeek
        const { abbreviation: weekAbv, season } = currentEventWeek

        // Get SDK Player props
        const result = await fetchWithBQ(
          footballEndpoints.getSDKPlayerPropsBySeasonWeek({
            sportsbookId: arg.sportsbookId,
            season,
            weekAbv,
            detailed: arg.detailed,
          })
        )

        if (result.error) {
          return { error: result.error }
        }

        const { analysis: playerPropAnalysis } = result.data as { analysis: SDKPlayerPropGradeAnalysis[] }

        return { data: playerPropAnalysis || [] }
      },
      keepUnusedDataFor: 7200,
    }),
    getSDKRecentDefensesForEvent: builder.query<
      {
        historicalData: PlayerHistoricalPerformance
        playerTeamsRecentDefenses: RecentDefensesForTeam
      },
      { eventId: number; playerId: number; playerTeamId: number; propKey: PlayerPropKey }
    >({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        try {
          const recentDefensesForEvent = await PffKitService.getRecentDefensesForEvent(arg.eventId)

          const playerTeamsRecentDefenses = recentDefensesForEvent.recentDefenses.find(
            (team) => team.teamId === arg.playerTeamId
          )

          if (!playerTeamsRecentDefenses) {
            throw new Error(`No similar defences found for players team ${arg.playerTeamId}`)
          }

          const recentDefenseEventIds = playerTeamsRecentDefenses.recentDefenses.map(
            (recentDefense) => recentDefense.eventId
          )

          const historicalPerformanceRequest = await fetchWithBQ(
            footballEndpoints.getSDKHistoricalPerformance({
              playerId: arg.playerId,
              propKey: arg.propKey,
              eventIds: recentDefenseEventIds,
            })
          )
          if (historicalPerformanceRequest.error) return { error: historicalPerformanceRequest.error }

          return {
            data: {
              historicalData: historicalPerformanceRequest.data,
              playerTeamsRecentDefenses,
            },
          } as {
            data: {
              historicalData: PlayerHistoricalPerformance
              playerTeamsRecentDefenses: RecentDefensesForTeam
            }
          }
        } catch (e: PFFKitError | any) {
          return e instanceof PFFKitError ? { error: e.message } : { error: e }
        }
      },
      keepUnusedDataFor: 7200,
    }),
    getMismatches: builder.query<NFLMismatch[], { id: number; sportsbookId: string }>({
      query: footballEndpoints.getMismatchesForGame,
      // Temporary way to filter mismatches, some of which are missing
      // have player data missing from the API
      transformResponse: (response: { mismatches: NFLMismatch[] }) => filterMismatches(response.mismatches ?? []),
    }),
    getPlayer: builder.query<FootballPlayer, { id: number }>({
      async queryFn(arg) {
        try {
          const player = await PffKitService.getPlayer(arg?.id)
          return { data: player }
        } catch (e: PFFKitError | any) {
          return e instanceof PFFKitError ? { error: e.message } : { error: e }
        }
      },
    }),
    getRankerInsights: builder.query({
      keepUnusedDataFor: 1800,
      query: ({ playerId, scoringType, leagueType }) => {
        // TODO remove once we support league types better
        const params = {
          playerId,
          scoringType,
          leagueType,
        }
        if (params.leagueType === FantasyLeagueType.TWO_QB) {
          params.leagueType = FANTASY_CONSTANTS.API_LEAGUE_TYPE[FantasyLeagueType.STANDARD]
          params.scoringType = 'superflex'
        }
        const queryParams = new URLSearchParams(params)
        return `${fantasyEndpoints.rankerInsights()}?${queryParams}`
      },
      transformResponse: (response: RankWithPlayerId[]): RankWithPlayerId[] => {
        return response.map((rankData) => ({
          ...rankData,
          rank: rankData.rank || 'NR',
        }))
      },
    }),
    getRankings: builder.query<RankingsResponse, InSeasonFiltersEntity | FiltersEntity | PlayerFiltersEntity>({
      keepUnusedDataFor: 1800,
      query: (filters) => {
        const prunedFilters: any = {}
        Object.entries(filters).forEach(([filterKey, value]) => {
          const shouldInclude = Array.isArray(value) ? value.length !== 0 : Boolean(value)
          if (shouldInclude) {
            prunedFilters[filterKey] = value
          }
        })

        const hasFlex = (prunedFilters?.position || []).includes(FantasyPosition.FLEX)
        if (hasFlex) {
          // We need to make sure that we send unique positions to BE, so we use a set. This usecase happens if someone selects FL and then TE
          prunedFilters.position = [
            ...new Set(
              prunedFilters.position
                .filter((pos: FantasyPositions) => pos !== FantasyPosition.FLEX)
                .concat([FantasyPosition.RB, FantasyPosition.WR, FantasyPosition.TE])
            ),
          ]
        }

        // TODO remove once we support league types better
        if (prunedFilters.leagueType === FantasyLeagueType.TWO_QB) {
          prunedFilters.leagueType = FANTASY_CONSTANTS.API_LEAGUE_TYPE[FantasyLeagueType.STANDARD]
          prunedFilters.scoringType = FantasyScoringTypeEnum.REDRAFT_2QB_PPR
        } else if (prunedFilters.scoringType === FANTASY_CONSTANTS.API_SCORING_TYPE[FantasyScoringType.PPR]) {
          prunedFilters.scoringType = FantasyScoringTypeEnum.REDRAFT_PPR
        } else if (
          prunedFilters.scoringType === FANTASY_CONSTANTS.API_SCORING_TYPE[FantasyScoringType.ZERO_POINT_FIVE_PPR]
        ) {
          prunedFilters.scoringType = FantasyScoringTypeEnum.REDRAFT_HALF_PPR
        } else if (prunedFilters.scoringType === FANTASY_CONSTANTS.API_SCORING_TYPE[FantasyScoringType.STANDARD]) {
          prunedFilters.scoringType = FantasyScoringTypeEnum.REDRAFT_NON_PPR
        }

        // The tags property is removed from the filters because filtering based on tags is now moved to the front end
        Reflect.deleteProperty(prunedFilters, 'tags')

        const params = new URLSearchParams(prunedFilters)
        return `${fantasyEndpoints.rankings()}?${params}`
      },
      transformResponse: transformRankingsResponse,
    }),
    getRankingsForMds: builder.query({
      keepUnusedDataFor: 1800,
      query: (filters) => {
        const prunedFilters: any = {}
        Object.keys(filters).forEach((filterKey) => {
          const value = filters[filterKey]
          if (Array.isArray(value)) {
            if (value.length !== 0) {
              prunedFilters[filterKey] = value
            }
          } else if (value) {
            prunedFilters[filterKey] = value
          }
        })

        const hasFlex = (prunedFilters?.position || []).includes(FantasyPosition.FLEX)
        if (hasFlex) {
          // We need to make sure that we send unique positions to BE, so we use a set. This usecase happens if someone selects FL and then TE
          prunedFilters.position = [
            ...new Set(
              prunedFilters.position
                .filter((pos: FantasyPositions) => pos !== FantasyPosition.FLEX)
                .concat([FantasyPosition.RB, FantasyPosition.WR, FantasyPosition.TE])
            ),
          ]
        }

        // TODO remove once we support league types better
        if (prunedFilters.leagueType === FantasyLeagueType.TWO_QB) {
          prunedFilters.leagueType = FANTASY_CONSTANTS.API_LEAGUE_TYPE[FantasyLeagueType.STANDARD]
          prunedFilters.scoringType = FantasyScoringTypeEnum.REDRAFT_2QB_PPR
        } else if (prunedFilters.scoringType === FANTASY_CONSTANTS.API_SCORING_TYPE[FantasyScoringType.PPR]) {
          prunedFilters.scoringType = FantasyScoringTypeEnum.REDRAFT_PPR
        } else if (
          prunedFilters.scoringType === FANTASY_CONSTANTS.API_SCORING_TYPE[FantasyScoringType.ZERO_POINT_FIVE_PPR]
        ) {
          prunedFilters.scoringType = FantasyScoringTypeEnum.REDRAFT_HALF_PPR
        } else {
          prunedFilters.scoringType = FantasyScoringTypeEnum.REDRAFT_NON_PPR
        }

        // The tags property is removed from the filters because filtering based on tags is now moved to the front end
        Reflect.deleteProperty(prunedFilters, 'tags')

        const params = new URLSearchParams(prunedFilters)
        return `${fantasyEndpoints.rankings()}?${params}`
      },
      transformResponse: transformRankingsResponse,
    }),
    getWeeklyRankings: builder.query<WeeklyRankingsResponse, InSeasonFiltersEntity | FiltersEntity>({
      keepUnusedDataFor: 1800,
      query: (filters) => {
        const prunedFilters: any = {}
        Object.entries(filters).forEach(([filterKey, value]) => {
          const shouldInclude = Array.isArray(value) ? value.length !== 0 : Boolean(value)
          if (shouldInclude) {
            prunedFilters[filterKey] = value
          }
        })

        if (prunedFilters?.position === FantasyPosition.SFL) {
          prunedFilters.position = FantasyPosition.FLEX
          prunedFilters.scoringType = `${prunedFilters.scoringType}Superflex`
        }

        const params = new URLSearchParams(prunedFilters)
        return `${fantasyEndpoints.weeklyRankings()}?${params}`
      },
      transformResponse: transformWeeklyRankingsResponse,
    }),
    getWaiverTargets: builder.query<WaiverTargets, void>({
      keepUnusedDataFor: 1800,
      query: () => `${fantasyEndpoints.waiverTargets()}`,
      transformResponse: transformWaiverTargetsResponse,
    }),
    getStartSitRankings: builder.query<WeeklyRankingsResponse, InSeasonFiltersEntity>({
      keepUnusedDataFor: 1800,
      query: (filters) => {
        const prunedFilters: any = {}
        Object.entries(filters).forEach(([filterKey, value]) => {
          const shouldInclude = Array.isArray(value) ? value.length !== 0 : Boolean(value)
          if (shouldInclude) {
            prunedFilters[filterKey] = value
          }
        })

        const params = new URLSearchParams(prunedFilters)
        return `${fantasyEndpoints.weeklyRankings()}?${params}`
      },
      transformResponse: transformWeeklyRankingsResponse,
    }),
    getMatchups: builder.query({
      keepUnusedDataFor: 1800,
      query: ({ page = 1, pageSize = 350, position = 'wr_cb' }) => {
        // TODO add back search once fuzzy search works
        const params = new URLSearchParams({ page, pageSize })
        return `${fantasyEndpoints.matchups()}/${position}?${params}`
      },
      transformResponse: (response: any) => {
        return {
          currentGameWeek: response.currentGameWeek,
          matchups: response.matchups.map((matchup: any) => ({
            id: matchup.playerId,
            first_name: matchup.firstName,
            last_name: matchup.lastName,
            long_name: `${matchup.firstName} ${matchup.lastName}`,
            position: matchup.position,
            team: matchup.teamAbbreviation,
            opposition: matchup.nextOpponentAbbreviation,
            isHomeGame: matchup.isHomeGame,
            matchTime: matchup.nextGameTime,
            gameWeek: matchup.nextGameWeek,
            matchup: {
              overallAdvantage: matchup.matchup.overallAdvantage,
              matchupPlayers: matchup.matchup.matchupPlayers.map((matchupPlayer: any) => ({
                playerId: matchupPlayer.playerId,
                advantageAgainst: matchupPlayer.advantageAgainst,
                projectedSnaps: matchupPlayer.projectedSnaps,
                first_name: matchupPlayer.firstName,
                last_name: matchupPlayer.lastName,
              })),
              // We add these onto the object so we can search for cornerbacks within matchups
              matchupPlayer1: `${matchup.matchup.matchupPlayers[0].firstName} ${matchup.matchup.matchupPlayers[0].lastName}`,
              matchupPlayer2: `${matchup.matchup.matchupPlayers[1].firstName} ${matchup.matchup.matchupPlayers[1].lastName}`,
              matchupPlayer3: `${matchup.matchup.matchupPlayers[2].firstName} ${matchup.matchup.matchupPlayers[2].lastName}`,
            },
          })),
        }
      },
    }),
    getTeams: builder.query<NFLTeam[], GetTeamsParams | void>({
      query: (params) => footballEndpoints.getTeamsForSeason(params),
      transformResponse: (response: { teams: NFLTeam[] }) => {
        return response.teams ?? []
      },
    }),
    getCurrentWeekPlayerPropBets: builder.query<PlayerPropWithGradeAnalysis[], { sportsbookId: SportsbookType }>({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        const getCurrentWeek = await fetchWithBQ(footballEndpoints.getCurrentEventWeek({ league: PlayerLeague.NFL }))
        if (getCurrentWeek.error) return { error: getCurrentWeek.error }
        const currentEventWeek = getCurrentWeek.data as SDKWeek
        const { abbreviation: weekAbv, season } = currentEventWeek

        // Get player props with market data
        const getPropBets = await fetchWithBQ(
          footballEndpoints.getPlayerPropBets({
            sportsbookId: arg.sportsbookId,
            weekAbv,
            season,
          })
        )

        if (getPropBets.error) return { error: getPropBets.error }

        const { playerProps: playerPropsBetsData } = getPropBets.data as {
          playerProps: PlayerPropWithGradeAnalysis[]
        }

        const playerPropBets = transformPlayerPropBetsResponse(
          (playerPropsBetsData || []) as PlayerPropWithGradeAnalysis[]
        )
        return { data: playerPropBets }
      },
      keepUnusedDataFor: 7200,
    }),
    getGameTeamGrading: builder.query<NFLTeamRanking[], { id: number }>({
      query: footballEndpoints.getTeamGradingForGame,
      transformResponse: (response: { teamRankings: NFLTeamRanking[] }) => response.teamRankings ?? [],
    }),
    getFunFacts: builder.query<FunFacts[], void>({
      query: footballEndpoints.getFunFacts,
    }),
    getEventsWithInsights: builder.query<{ [key: number]: Insight[] }, { eventIds: string }>({
      query: footballEndpoints.getEventsWithInsights,
      transformResponse: (response: { eventId: number; insights: Insight[] }[]) => {
        const eventMap: { [key: number]: Insight[] } = {}

        response.forEach((event) => {
          eventMap[event.eventId as number] = event.insights
        })

        return eventMap
      },
    }),
    getDraftBigBoardMetadata: builder.query<BigBoardMetaResponse, void>({
      keepUnusedDataFor: 0,
      query: footballEndpoints.getDraftBigBoardMetadata,
    }),
    getDraftBigBoardData: builder.query<BigBoardResponse, void>({
      query: footballEndpoints.getDraftBigBoardData,
    }),
    getCurrentEventWeek: builder.query<SDKWeek, { league: PlayerLeague }>({
      query: footballEndpoints.getCurrentEventWeek,
    }),
    getSDKEventsByWeek: builder.query<
      { events: SDKEvent[]; weekAbv?: NFLWeekAbbreviation },
      { sportsbookId?: string; season?: number; weekAbv?: NFLWeekAbbreviation; scoreData?: boolean }
    >({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        try {
          let { weekAbv, season } = arg
          const { sportsbookId, scoreData } = arg
          if (!weekAbv) {
            const getCurrentWeek = await fetchWithBQ(
              footballEndpoints.getCurrentEventWeek({ league: PlayerLeague.NFL })
            )
            if (getCurrentWeek.error) return { error: getCurrentWeek.error }
            const currentEventWeek = getCurrentWeek.data as SDKWeek
            weekAbv = currentEventWeek.abbreviation
            season = currentEventWeek.season
          }

          const events = await PffKitService.getEvents(season!, sportsbookId, weekAbv, scoreData)
          return { data: { events, weekAbv } }
        } catch (e: PFFKitError | any) {
          return e instanceof PFFKitError ? { error: e.message } : { error: e }
        }
      },
    }),
    getCurrentWeekEvents: builder.query<SDKEvent[], { sportsbookId: string; scoreData?: boolean }>({
      async queryFn({ sportsbookId, scoreData }, _queryApi, _extraOptions, fetchWithBQ) {
        try {
          const getCurrentWeek = await fetchWithBQ(footballEndpoints.getCurrentEventWeek({ league: PlayerLeague.NFL }))

          if (getCurrentWeek.error) return { error: getCurrentWeek.error }

          const currentEventWeek = getCurrentWeek.data as SDKWeek
          const { abbreviation: weekAbv, season } = currentEventWeek

          const events = await PffKitService.getEvents(season, sportsbookId, weekAbv, scoreData)

          return { data: events }
        } catch (e: PFFKitError | any) {
          return e instanceof PFFKitError ? { error: e.message } : { error: e }
        }
      },
      keepUnusedDataFor: 7200,
    }),
    getCurrentWeekEventsWithMarketData: builder.query<
      Partial<BettingNFLEvent>[],
      { sportsbookId: SportsbookType; scoreData?: boolean }
    >({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        try {
          const { sportsbookId, scoreData } = arg
          const getCurrentWeek = await fetchWithBQ(footballEndpoints.getCurrentEventWeek({ league: PlayerLeague.NFL }))
          if (getCurrentWeek.error) return { error: getCurrentWeek.error }
          const currentEventWeek = getCurrentWeek.data as SDKWeek
          const { abbreviation: weekAbv, season } = currentEventWeek

          const currentWeekEvents = await PffKitService.getEvents(season, sportsbookId, weekAbv, scoreData)

          if (!currentWeekEvents || currentWeekEvents.length === 0) {
            return { data: [] }
          }

          const getEventsMarketDataByWeek = (await fetchWithBQ(
            footballEndpoints.getEventsMarketDataByWeek({
              sportsbookId: arg.sportsbookId,
              season,
              weekAbv,
            })
          )) as { data: { markets: EventMarketData[] }; error?: FetchBaseQueryError }

          if (getEventsMarketDataByWeek.error) {
            return { error: getEventsMarketDataByWeek.error }
          }
          const currentWeekEventsMarketData = getEventsMarketDataByWeek.data.markets

          return { data: addEventMarketsDataToEvents(currentWeekEvents, currentWeekEventsMarketData, arg.sportsbookId) }
        } catch (e: PFFKitError | any) {
          return e instanceof PFFKitError ? { error: e.message } : { error: e }
        }
      },
      keepUnusedDataFor: 7200,
    }),
    getInsightsByEvent: builder.query<
      { playerInsights: ConnectInsightWithPlayer[]; players: FootballPlayer[] },
      { eventId: string }
    >({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        const getConnectInsights = (await fetchWithBQ(
          connectEndpoints.getInsightsByEvents({ eventIds: arg.eventId })
        )) as { data: { connectInsights: ConnectInsight[] }[]; error?: FetchBaseQueryError }

        if (getConnectInsights.error) return { error: getConnectInsights.error }

        const { connectInsights } = getConnectInsights.data[0]
        if (!connectInsights || connectInsights.length === 0) return { data: { playerInsights: [], players: [] } }

        const ids = connectInsights.map((insights) => insights.playerId)

        const getPlayers = (await fetchWithBQ(
          footballEndpoints.getPlayersById({
            ids,
          })
        )) as { data: { players: FootballPlayer[] }; error?: FetchBaseQueryError }

        if (getPlayers.error) {
          return { error: getPlayers.error }
        }

        return { data: getInsightWithPlayer(connectInsights, getPlayers.data.players) }
      },
      keepUnusedDataFor: 7200,
    }),
    getPlayerInsightsByWeek: builder.query<PlayerInsightsByWeek, { week: NFLWeekAbbreviation; season: number }>({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        const { week, season } = arg
        const keyInsightsResponse = (await fetchWithBQ(footballEndpoints.getGameScripts({ week, season }))) as {
          data: { scripts: GameScript[] }
          error?: FetchBaseQueryError
        }

        if (keyInsightsResponse.error) return { error: keyInsightsResponse.error }

        const { scripts } = keyInsightsResponse.data
        if (!scripts || scripts.length === 0) return { data: { keyInsights: [], players: [] } }

        const ids = scripts.map((script) => script.players[0].playerId)

        const getPlayersRequest = fetchWithBQ(
          footballEndpoints.getPlayersById({
            ids,
          })
        )

        const getGamesByWeekRequest = fetchWithBQ(footballEndpoints.getSeasonGames({ season, week }))

        const [getPlayers, getGamesByWeek] = await Promise.all([getPlayersRequest, getGamesByWeekRequest])

        const playersList = getPlayers.data as { players: FootballPlayer[] }
        const gamesByWeek = getGamesByWeek.data as { games: FootballGameV2[] }

        if (getPlayers.error) {
          return { error: getPlayers.error }
        }

        if (getGamesByWeek.error) {
          return { error: getGamesByWeek.error }
        }

        return { data: getGameScriptWithPlayer(scripts, playersList.players, gamesByWeek.games) }
      },
      keepUnusedDataFor: 7200,
    }),
    getAvailableMarketsByEvent: builder.query<
      PlayerPropKey[],
      {
        sportsbookId: string
        eventId: number
        eventStatus?: string
      }
    >({
      query: footballEndpoints.getAvailableMarketsByEvent,
      transformResponse: (response: { eventId: number; markets: PlayerPropKey[] }) => {
        return response.markets || []
      },
    }),
    getAvailableMarketsByWeek: builder.query<
      PlayerPropKey[],
      {
        sportsbookId: string
        weekAbv: string
        eventStatus?: string
      }
    >({
      query: footballEndpoints.getAvailableMarketsByWeek,
      transformResponse: (response: { eventId: number; markets: PlayerPropKey[] }) => {
        return response.markets || []
      },
    }),
    getTopPlayersForEvent: builder.query<SDKTopPlayerProp[], { eventId: number; sportsbookId: string }>({
      query: footballEndpoints.getTopPlayersForEvent,
    }),
    getPaginatedPropBetsWithAnalysisByEvents: builder.query<
      PlayerPropSection[],
      { sportsbookId: SportsbookType; eventIds: number[]; weekAbv: string; page?: number; propKeys: PlayerPropKey[] }
    >({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        const { sportsbookId, eventIds, page, propKeys, weekAbv } = arg

        const getPropBets = await Promise.all(
          propKeys.map((key) => {
            const pageSize = PROPS_WITH_OVER_UNDER.includes(key) ? OVER_UNDER_MARKET_PAGE_SIZE : SINGLE_MARKET_PAGE_SIZE
            const eventStatus = weekAbv === 'SB' ? SUPER_BOWL_EVENT_STATUS : ''
            return fetchWithBQ(
              footballEndpoints.getPaginatedPropBetsByEvents({
                sportsbookId,
                eventIds,
                page,
                market: key,
                pageSize,
                eventStatus,
              })
            ) as { data: PaginatedPlayerPropBets; error?: FetchBaseQueryError }
          })
        )

        const getPropBetsError = getPropBets.find(({ error }) => !!error)

        if (getPropBetsError?.error) {
          return { error: getPropBetsError.error }
        }

        const { propBets, sportsbookBetIds } = combinePaginatedResults(getPropBets.map((x) => x.data))

        const analysisDetails = (await fetchWithBQ(
          footballEndpoints.getAnalysisByBetId({ sportsbookIds: sportsbookBetIds.toString(), weekAbv, detailed: true })
        )) as unknown as { data: { analysis: SDKPlayerPropGradeAnalysis[] }; error?: FetchBaseQueryError }

        if (analysisDetails.error) {
          return { error: analysisDetails.error }
        }

        const propBetsWithAnalysis = getPropBetsWithAnalysis(propBets, analysisDetails.data.analysis)

        return { data: groupPropBetsByPropKey(propBetsWithAnalysis) }
      },
      keepUnusedDataFor: 7200,
    }),
    getInsightsUpdatedTimestampForCurrentWeek: builder.query<InsightsUpdatedTimestamp[], void>({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        const getCurrentWeek = await fetchWithBQ(footballEndpoints.getCurrentEventWeek({ league: PlayerLeague.NFL }))

        if (getCurrentWeek.error) return { error: getCurrentWeek.error }

        const currentEventWeek = getCurrentWeek.data as SDKWeek

        const getLastUpdatedInsights = (await fetchWithBQ(
          connectEndpoints.getInsightsUpdatedTimestampByWeek({ weekAbv: currentEventWeek.abbreviation })
        )) as { data: { lastUpdatedInsights: InsightsUpdatedTimestamp[] }; error?: FetchBaseQueryError }

        if (getLastUpdatedInsights.error) return { error: getLastUpdatedInsights.error }

        const { lastUpdatedInsights } = getLastUpdatedInsights.data

        return { data: lastUpdatedInsights }
      },
    }),
    getTeamDepthChart: builder.query<
      TeamDepthChart,
      {
        league?: string
        season?: number
        weekAbv: string
        teamId: number
      }
    >({
      query: footballEndpoints.getTeamDepthChart,
      transformResponse: (response: {
        season: number
        week: string
        teamId: number
        depthChart: Record<string, number[]>
      }) => {
        return response || {}
      },
    }),
    getPlayersByIds: builder.query<FootballPlayer[], { ids: number[] }>({
      query: footballEndpoints.getPlayersById,
      transformResponse: (response: { players: FootballPlayer[] }) => {
        return response.players || {}
      },
    }),
    getPlayerById: builder.query<FootballPlayerWithGradesTeamAndDraftInfo, { id: number }>({
      query: footballEndpoints.getPlayerById,
      transformResponse: (response: FootballPlayerWithGradesTeamAndDraftInfo) => {
        return response || {}
      },
    }),
    getPlayersByName: builder.query<FootballPlayerWithGradesAndTeam[], { name: string }>({
      query: footballEndpoints.getPlayersByName,
      transformResponse: (response: { players: FootballPlayerWithGradesAndTeam[] }) => {
        return response.players || []
      },
    }),
    getPlayerGradesByIds: builder.query<PlayerPositionGrade[], { ids: number[] }>({
      query: footballEndpoints.getPlayerGradesByIds,
      transformResponse: (response: { players: PlayerPositionGrade[] }) => {
        return response.players || []
      },
    }),
    getPlayerInjuryStatus: builder.query<InjuryData[], { season: number; weekAbv: string; eventIds?: number[] }>({
      query: footballEndpoints.getPlayerInjuryStatus,
      transformResponse: (response: { events: InjuryData[] }) => {
        return response.events || []
      },
    }),
    getPlayerInjuryStatusFantasy: builder.query<PlayersInjuryRecord, void>({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        const getCurrentWeek = await fetchWithBQ(footballEndpoints.getCurrentEventWeek({ league: PlayerLeague.NFL }))

        if (getCurrentWeek.error) return { error: getCurrentWeek.error }

        const currentEventWeek = getCurrentWeek.data as SDKWeek

        const getInjuryData = (await fetchWithBQ(
          footballEndpoints.getPlayerInjuryStatus({
            season: currentEventWeek.season,
            league: League.NFL,
            weekAbv: currentEventWeek.abbreviation,
          })
        )) as { data: { events: InjuryData[] }; error?: FetchBaseQueryError }

        if (getInjuryData.error) return { error: getInjuryData.error } as { error: FetchBaseQueryError }

        const injuryData = getInjuryData.data.events

        return { data: transformPlayerInjuryStatusResponse(injuryData) }
      },
      keepUnusedDataFor: 1800,
    }),
    getPlayersByPositions: builder.query<FootballPlayerWithGradesAndTeam[], { positions: string[]; limit?: number }>({
      query: footballEndpoints.getPlayersByPositions,
      transformResponse: (response: { players: FootballPlayerWithGradesAndTeam[] }) => {
        return response.players || []
      },
    }),
    getPlayersByTeams: builder.query<
      FootballPlayerWithGradesAndTeam[],
      { teamIds: number[]; limit?: number; league?: string }
    >({
      query: footballEndpoints.getPlayersByTeams,
      transformResponse: (response: { players: FootballPlayerWithGradesAndTeam[] }) => {
        return response.players || []
      },
    }),
    getBigBoardData: builder.query<DraftEligiblePlayer[], { limit?: number }>({
      query: footballEndpoints.getDraftBigBoardData,
      transformResponse: (response: { players: DraftEligiblePlayer[] }) => {
        return response.players || []
      },
    }),
    getGameScripts: builder.query<
      {
        season: number
        week: NFLWeekAbbreviation
        scripts: GameScript[]
      },
      { season: number; week: NFLWeekAbbreviation; playerIds: number[] }
    >({
      query: footballEndpoints.getGameScripts,
      transformResponse: (response: { season: number; week: NFLWeekAbbreviation; scripts: GameScript[] }) => {
        return response || {}
      },
    }),
    getSeasonGames: builder.query<FootballGameV2[], GetSeasonGamesParams>({
      query: footballEndpoints.getSeasonGames,
      transformResponse: (response: { games: FootballGameV2[] }) => {
        return response.games || null
      },
    }),
    getPremiumStatsPreviewKeyStats: builder.query<
      PremiumStatsPreviewKeyStatsResponse,
      PremiumStatsPreviewKeyStatsParams
    >({
      query: (params) => {
        const { league, season, weekGroup, playerId } = params
        return {
          url: footballEndpoints.getPremiumStatsPreviewKeyStats().url,
          params: { league, season, weekGroup, playerId },
        }
      },
    }),
    getWeekProjection: builder.query<FantasyWeekProjectionResponse, FantasyWeekProjectionParams>({
      query: footballEndpoints.getWeekProjection,
      transformResponse: (response: { projection: FantasyWeekProjection }) => {
        return response || null
      },
    }),
    getCareer: builder.query<PlayerCareer, { playerId: number }>({
      query: footballEndpoints.getCareer,
      transformResponse: (response: PlayerCareer) => {
        return response || null
      },
    }),
    getGradesByGame: builder.query<MappedGradesByGameResponse, { playerId: string | number }>({
      query: footballEndpoints.getGradesByGame,
      transformResponse: (response: MappedGradesByGameResponse) => {
        return response || null
      },
    }),
    getPlayerProfileBaseData: builder.query<PlayerProfileBaseDataResponse, { playerId: number; league: PlayerLeague }>({
      async queryFn(arg, _queryApi, _extraOptions, fetchWithBQ) {
        const { playerId, league } = arg
        const getPlayerRequest = fetchWithBQ(footballEndpoints.getPlayerById({ id: playerId }))
        const getCurrentEventWeekRequest = fetchWithBQ(footballEndpoints.getCurrentEventWeek({ league }))
        const getCareerRequest = fetchWithBQ(footballEndpoints.getCareer({ playerId }))

        const [getPlayerResponse, getCurrentEventWeekResponse, getCareerResponse] = await Promise.all([
          getPlayerRequest,
          getCurrentEventWeekRequest,
          getCareerRequest,
        ])

        if (getPlayerResponse.error) return { error: getPlayerResponse.error }
        if (getCurrentEventWeekResponse.error) return { error: getCurrentEventWeekResponse.error }
        if (getCareerResponse.error) return { error: getCareerResponse.error }

        const player = getPlayerResponse.data as FootballPlayerWithGradesTeamAndDraftInfo
        let career = getCareerResponse.data as PlayerCareer

        career = addActiveSeasonsForRookie(career, player)

        return {
          data: {
            player,
            currentEventWeek: getCurrentEventWeekResponse.data as SDKWeek,
            career,
          },
        }
      },
      keepUnusedDataFor: 1800,
    }),
    getPlayerProps: builder.query<GetPlayerPropsByWeekResponse, GetPlayerPropsParams>({
      query: bettingEndpoints.getPlayerProps,
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetSDKEventsQuery,
  useGetCurrentEventWeekPlayerPropsAnalysisQuery,
  useGetSDKMismatchesBySeasonWeekQuery,
  useGetSDKHotPlayersBySeasonWeekQuery,
  useGetSDKHotPlayersByEventQuery,
  useGetMismatchesQuery,
  useGetPlayerQuery,
  useGetLatestArticlesQuery,
  useGetArticleByIdOrSlugQuery,
  useGetBookmarkedArticlesQuery,
  useGetLandingPageContentQuery,
  useGetFeedCategoriesQuery,
  useGetFeedItemsQuery,
  useGetTeamsQuery,
  useGetRankingsQuery,
  useGetRankingsForMdsQuery,
  useGetWeeklyRankingsQuery,
  useGetWaiverTargetsQuery,
  useGetStartSitRankingsQuery,
  useGetMatchupsQuery,
  useGetGameTeamGradingQuery,
  useGetFunFactsQuery,
  useGetCurrentWeekPlayerPropBetsQuery,
  useGetEventsWithInsightsQuery,
  useLazyGetDraftBigBoardMetadataQuery,
  useLazyGetDraftBigBoardDataQuery,
  util: { getRunningQueriesThunk },
  useGetPlayerMatchupsForEventQuery,
  useGetSDKMismatchesByEventQuery,
  useGetSDKRecentDefensesForEventQuery,
  useGetCurrentEventWeekQuery,
  useGetCurrentWeekAndEventsQuery,
  useGetCurrentWeekEventsQuery,
  useGetCurrentWeekEventsWithMarketDataQuery,
  useGetSDKEventsByIdQuery,
  useGetSDKEventsByWeekQuery,
  useGetSDKEventsWithMarketByIdQuery,
  useGetInsightsByEventQuery,
  useGetPlayerInsightsByWeekQuery,
  useGetTopPlayersForEventQuery,
  useGetAvailableMarketsByEventQuery,
  useGetAvailableMarketsByWeekQuery,
  useGetInsightsUpdatedTimestampForCurrentWeekQuery,
  useGetTeamDepthChartQuery,
  useGetPlayerByIdQuery,
  useGetPlayersByNameQuery,
  useLazyGetPlayersByNameQuery,
  useLazyGetPlayersByIdsQuery,
  useGetPlayerGradesByIdsQuery,
  useGetSDKPlayerMatchupsForWeekQuery,
  useGetSDKPlayerMatchupsQuery,
  useGetSDKReceiverMatchupsForWeekQuery,
  useGetSDKReceiverMatchupsQuery,
  useGetSDKMatchupsQuery,
  useGetSDKReceivingMetricsQuery,
  useGetSDKHalftimeReceivingMetricsQuery,
  useGetSDKRushingMetricsQuery,
  useGetSDKHalftimeRushingMetricsQuery,
  useGetPFFLiveDataQuery,
  useGetPlayerProfileHistoricalPerformanceDataQuery,
  useGetPreGamePassNarrativesQuery,
  useGetPlayerInjuryStatusQuery,
  useGetPlayerInjuryStatusFantasyQuery,
  useGetPlayersByPositionsQuery,
  useGetPlayersByTeamsQuery,
  useGetBigBoardDataQuery,
  useGetSeasonGamesQuery,
  useGetGameScriptsQuery,
  useGetPremiumStatsPreviewKeyStatsQuery,
  useGetWeekProjectionQuery,
  useGetCareerQuery,
  useGetGradesByGameQuery,
  useGetPlayerProfileBaseDataQuery,
  useGetPlayerPropsQuery,
} = consumerApi

// Export getters to be used when rendering components on the server
export const {
  getSDKEvents,
  getCurrentEventWeekPlayerPropsAnalysis,
  getSDKMismatchesBySeasonWeek,
  getSDKHotPlayersBySeasonWeek,
  getSDKHotPlayersByEvent,
  getRankings,
  getRankingsForMds,
  getMatchups,
  getWeeklyRankings,
  getWaiverTargets,
  getStartSitRankings,
  getRankerInsights,
  getTeams,
  getMismatches,
  getLatestArticles,
  getBookmarkedArticles,
  getArticleByIdOrSlug,
  getLandingPageContent,
  getFeedCategories,
  getFeedItems,
  getPlayer,
  getPlayerById,
  getPlayersByIds,
  getPlayersByName,
  getGameTeamGrading,
  getFunFacts,
  getCurrentWeekPlayerPropBets,
  getEventsWithInsights,
  getDraftBigBoardMetadata,
  getDraftBigBoardData,
  getPlayerMatchupsForEvent,
  getSDKMismatchesByEvent,
  getSDKRecentDefensesForEvent,
  getCurrentEventWeek,
  getCurrentWeekAndEvents,
  getCurrentWeekEvents,
  getCurrentWeekEventsWithMarketData,
  getSDKEventsById,
  getSDKEventsByWeek,
  getSDKEventsWithMarketById,
  getTopPlayersForEvent,
  getAvailableMarketsByEvent,
  getPaginatedPropBetsWithAnalysisByEvents,
  getTeamDepthChart,
  getPlayerGradesByIds,
  getSDKPlayerMatchupsForWeek,
  getSDKPlayerMatchups,
  getSDKReceiverMatchups,
  getSDKReceiverMatchupsForWeek,
  getSDKMatchups,
  getSDKReceivingMetrics,
  getSDKHalftimeReceivingMetrics,
  getSDKRushingMetrics,
  getSDKHalftimeRushingMetrics,
  getPFFLiveData,
  getSDKPFFPassRateNarratives,
  getSDKPFFPressureRateNarratives,
  getPlayerProfileHistoricalPerformanceData,
  getPreGamePassNarratives,
  getPlayerInjuryStatus,
  getPlayerInjuryStatusFantasy,
  getPlayersByPositions,
  getPlayersByTeams,
  getGameScripts,
  getWeekProjection,
  getCareer,
  getSeasonGames,
  getGradesByGame,
  getPlayerProfileBaseData,
  getPlayerProps,
} = consumerApi.endpoints
